import type { PublicUser, ReportReason } from '@sirocco/shared';
import { requireUid } from './auth';
import { estCompteOfficiel } from './officiel';
import { paths } from './paths';
import { loadUsers, unknownUser } from './records';
import { newKey, readOptionalKeys, readOptionalValue, SiroccoDataError, updateValues } from './rtdb';
import { t } from '../i18n';

/**
 * Blocage, masquage et signalement d'un membre.
 *
 * Bloquer retire l'autre de ce que *je* lis — mon fil, les reponses, mes
 * suggestions, mes recherches —, casse le lien d'amitie dans les deux
 * sens, et lui ferme ce qui m'atteint : il ne peut plus aimer mes posts
 * ni y repondre, ni m'ecrire, ni me demander en ami. Les regles et les
 * fonctions le refusent, pas seulement l'ecran. Il lit encore ce qui est
 * public ; le pretendre autrement serait mentir a l'utilisateur.
 *
 * Le signalement est archive dans `reports/` pour la moderation, et
 * bloque au passage : signaler quelqu'un puis continuer a le croiser
 * n'aurait aucun sens, et la moderation ne repond pas dans la seconde.
 */

/**
 * Les membres que j'ai bloques.
 * Lecture tolerante : la branche est recente, et tant que sa regle n'est
 * pas deployee un refus ne doit pas emporter les ecrans qui s'en servent
 * pour filtrer.
 */
export async function listBlocked(): Promise<string[]> {
  return readOptionalKeys(paths.blockedUsers(requireUid()));
}

/** La liste tient en quelques cles : la relire entiere coute moins qu'un index. */
export async function isBlocked(otherUid: string): Promise<boolean> {
  return (await listBlocked()).includes(otherUid);
}

/** Les profils des membres bloques, pour l'ecran qui les liste. */
export async function listBlockedProfiles(): Promise<PublicUser[]> {
  const ids = await listBlocked();
  if (ids.length === 0) return [];

  const users = await loadUsers(ids);
  return ids
    .map((id) => users.get(id) ?? unknownUser(id))
    .sort((a, b) => a.username.localeCompare(b.username, 'fr'));
}

/**
 * Bloque un membre.
 * L'amitie et les demandes en cours tombent avec lui : garder un lien
 * vers quelqu'un qu'on ne veut plus voir n'a pas de sens, et laisserait
 * son profil dans la liste des amis. Tant que le blocage tient, il ne
 * peut plus me redemander en ami — les regles refusent l'ecriture, et
 * `listRequests` ecarte ce qui aurait pu passer avant elles.
 */
export async function blockUser(otherUid: string): Promise<void> {
  await updateValues(await blockUpdates(requireUid(), otherUid));
}

/**
 * Notre tete-a-tete, s'il existe et qu'on en est bien les deux membres.
 *
 * Verifie avant d'ecrire : une mise a jour groupee est tout ou rien, et
 * un index de paire qui pointerait vers une conversation disparue ferait
 * refuser la marque — donc le blocage entier avec elle.
 */
async function notreTeteATete(uid: string, otherUid: string): Promise<string | null> {
  type Fil = { type?: string; members?: Record<string, true> };
  const cEstLui = (fil: Fil | null | undefined) =>
    Boolean(fil?.type === 'direct' && fil.members?.[uid] && fil.members?.[otherUid]);

  const id = await readOptionalValue<string>(paths.directConversation(uid, otherUid));
  if (id && cEstLui(await readOptionalValue<Fil>(paths.conversation(id)))) return id;

  // Les fils d'avant l'index de paire n'y figurent pas : on fouille ma liste.
  const miens = await readOptionalKeys(paths.userConversations(uid));
  const fils = await Promise.all(
    miens.map((mien) => readOptionalValue<Fil>(paths.conversation(mien))),
  );
  return miens.find((_, rang) => cEstLui(fils[rang])) ?? null;
}

/** Les chemins d'un blocage, partages avec le signalement. */
async function blockUpdates(uid: string, otherUid: string): Promise<Record<string, unknown>> {
  const fil = await notreTeteATete(uid, otherUid);
  return {
    [paths.blockedUser(uid, otherUid)]: true,
    /*
      Notre conversation se ferme a lui : il ne peut plus m'y ecrire. La
      regle des messages ne sait pas lire ma liste de blocages, c'est
      cette marque qui le lui dit. Sans fil existant, il n'y a rien a
      fermer : les regles lui refuseront d'en ouvrir un.
    */
    ...(fil ? { [paths.conversationFermee(fil, otherUid)]: true } : {}),
    [paths.friend(uid, otherUid)]: null,
    [paths.friend(otherUid, uid)]: null,
    [paths.friendRequest(uid, otherUid)]: null,
    [paths.friendRequest(otherUid, uid)]: null,
    [paths.sentRequest(uid, otherUid)]: null,
    [paths.sentRequest(otherUid, uid)]: null,
  };
}

/* --- Masquer un compte ---------------------------------------------- */

/**
 * Les comptes que j'ai masques.
 *
 * Masquer n'est pas bloquer. Bloquer rompt l'amitie, ferme la messagerie
 * et interdit les demandes ; masquer ne touche a rien de tout cela : on
 * cesse seulement de voir les publications et les sorties de quelqu'un,
 * sans qu'il le sache et sans rien defaire. C'est le geste pour qui
 * publie trop, pas pour qui derange.
 *
 * Lecture tolerante, comme les blocages : une regle pas encore deployee
 * ne doit pas emporter le fil ni la liste des sorties.
 */
export async function listHidden(): Promise<string[]> {
  return readOptionalKeys(paths.hiddenUsers(requireUid()));
}

export async function isHidden(otherUid: string): Promise<boolean> {
  return (await listHidden()).includes(otherUid);
}

/** Ceux dont je ne vois plus rien : bloques ou masques, le fil ne distingue pas. */
export async function listUnseen(): Promise<string[]> {
  const [bloques, masques] = await Promise.all([listBlocked(), listHidden()]);
  return [...new Set([...bloques, ...masques])];
}

/** Les profils des comptes masques, pour l'ecran qui les liste. */
export async function listHiddenProfiles(): Promise<PublicUser[]> {
  const ids = await listHidden();
  if (ids.length === 0) return [];

  const users = await loadUsers(ids);
  return ids
    .map((id) => users.get(id) ?? unknownUser(id))
    .sort((a, b) => a.username.localeCompare(b.username, 'fr'));
}

export async function hideUser(otherUid: string): Promise<void> {
  await updateValues({ [paths.hiddenUser(requireUid(), otherUid)]: true });
}

export async function unhideUser(otherUid: string): Promise<void> {
  await updateValues({ [paths.hiddenUser(requireUid(), otherUid)]: null });
}

/* --- Bloquer --------------------------------------------------------- */

/** Debloque : l'amitie, elle, ne revient pas d'elle-meme. */
export async function unblockUser(otherUid: string): Promise<void> {
  const uid = requireUid();
  const fil = await notreTeteATete(uid, otherUid);
  await updateValues({
    [paths.blockedUser(uid, otherUid)]: null,
    // La conversation se rouvre a lui, comme a n'importe qui.
    ...(fil ? { [paths.conversationFermee(fil, otherUid)]: null } : {}),
  });
}

/**
 * Signalement d'un membre : archive pour la moderation, et blocage.
 *
 * Les deux partent dans la meme ecriture. Signaler sans bloquer laissait
 * la personne signalee dans le feed, les suggestions et la messagerie le
 * temps qu'un humain regarde — c'est-a-dire indefiniment cote membre.
 */
export async function reportUser(
  userId: string,
  reason: ReportReason,
  details = '',
): Promise<string> {
  const reporterId = requireUid();

  /*
    On ne signale pas le compte de l'application a lui-meme.

    Le signalement va a l'equipe qui tient ce compte, et il bloque au
    passage — la porte d'entree vers l'assistance se refermerait donc
    d'un seul appui. Le geste n'a pas de destinataire : il n'est pas
    propose, et il est refuse s'il arrive quand meme.
  */
  if (await estCompteOfficiel(userId)) {
    throw new SiroccoDataError(
      t.moderation.erreurs.compteOfficiel,
      'compte-officiel',
    );
  }

  const reportId = newKey(paths.reports());

  await updateValues({
    [`${paths.reports()}/${reportId}`]: {
      type: 'user',
      userId,
      reporterId,
      reason,
      details: details.trim(),
      createdAt: Date.now(),
    },
    ...(await blockUpdates(reporterId, userId)),
  });
  return reportId;
}
